if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.modalContent = this.querySelector('.product-media-modal__content');
        this.previousButton = this.querySelector('[data-matchy-modal-prev]');
        this.nextButton = this.querySelector('[data-matchy-modal-next]');
        this.zoomStates = new WeakMap();
        this.scrollEndTimer = null;
        this.lastZoomToggleAt = 0;
        this.setupMobileGallery();
      }

      hide() {
        this.resetAllZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const openedMediaId = this.openedBy.getAttribute('data-media-id');
        this.querySelectorAll(`[data-media-id]:not([data-media-id="${openedMediaId}"])`).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${openedMediaId}"]`);
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        const container = this.modalContent || this.querySelector('[role="document"]');
        if (this.mobileMediaQuery.matches) {
          const activeIndex = this.mobileSlides.indexOf(activeMedia);
          this.resetAllZoom(activeMedia);
          window.requestAnimationFrame(() => this.goToIndex(Math.max(activeIndex, 0), false));
        } else {
          activeMedia.scrollIntoView();
          const mediaWidth = Number(activeMedia.width) || container.clientWidth;
          container.scrollLeft = (mediaWidth - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      get mobileSlides() {
        if (!this.modalContent) return [];
        return Array.from(this.modalContent.children).filter((element) => element.matches('[data-media-id]'));
      }

      setupMobileGallery() {
        if (!this.modalContent) return;

        const keepMobileGalleryOpen = (event) => {
          if (this.mobileMediaQuery.matches) event.stopPropagation();
        };

        this.previousButton?.addEventListener('click', () => this.navigateBy(-1));
        this.nextButton?.addEventListener('click', () => this.navigateBy(1));
        this.previousButton?.addEventListener('pointerup', keepMobileGalleryOpen);
        this.nextButton?.addEventListener('pointerup', keepMobileGalleryOpen);
        this.modalContent.addEventListener('pointerup', keepMobileGalleryOpen);
        this.modalContent.addEventListener('scroll', () => this.handleMobileScroll(), { passive: true });

        this.modalContent.querySelectorAll('img[data-media-id]').forEach((image) => {
          this.zoomStates.set(image, this.createZoomState());
          image.addEventListener('dblclick', (event) => {
            if (!this.mobileMediaQuery.matches) return;
            event.preventDefault();
            this.toggleZoom(image, event.clientX, event.clientY);
          });
          image.addEventListener('touchstart', (event) => this.handleTouchStart(event, image), { passive: false });
          image.addEventListener('touchmove', (event) => this.handleTouchMove(event, image), { passive: false });
          image.addEventListener('touchend', (event) => this.handleTouchEnd(event, image), { passive: false });
          image.addEventListener('touchcancel', () => this.finishTouch(image));
        });
      }

      createZoomState() {
        return {
          scale: 1,
          startScale: 1,
          startDistance: 0,
          panX: 0,
          panY: 0,
          startPanX: 0,
          startPanY: 0,
          startTouchX: 0,
          startTouchY: 0,
          isPinching: false,
          isPanning: false,
          moved: false,
          lastTapTime: 0,
          lastTapX: 0,
          lastTapY: 0,
        };
      }

      navigateBy(direction) {
        if (!this.mobileMediaQuery.matches) return;
        const slides = this.mobileSlides;
        if (slides.length < 2) return;
        const currentIndex = this.getCurrentIndex();
        const nextIndex = (currentIndex + direction + slides.length) % slides.length;
        this.resetAllZoom();
        this.goToIndex(nextIndex, true);
      }

      getCurrentIndex() {
        const slides = this.mobileSlides;
        const activeIndex = slides.findIndex((slide) => slide.classList.contains('active'));
        if (activeIndex >= 0) return activeIndex;
        if (!this.modalContent?.clientWidth) return 0;
        return Math.max(0, Math.min(slides.length - 1, Math.round(this.modalContent.scrollLeft / this.modalContent.clientWidth)));
      }

      goToIndex(index, smooth) {
        const slides = this.mobileSlides;
        const target = slides[index];
        if (!target || !this.modalContent) return;
        slides.forEach((slide, slideIndex) => slide.classList.toggle('active', slideIndex === index));
        this.modalContent.scrollTo({
          left: index * this.modalContent.clientWidth,
          behavior: smooth ? 'smooth' : 'auto',
        });
      }

      handleMobileScroll() {
        if (!this.mobileMediaQuery.matches || this.modalContent?.classList.contains('is-zoomed')) return;
        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(() => {
          if (!this.modalContent?.clientWidth) return;
          const index = Math.max(
            0,
            Math.min(this.mobileSlides.length - 1, Math.round(this.modalContent.scrollLeft / this.modalContent.clientWidth))
          );
          this.mobileSlides.forEach((slide, slideIndex) => slide.classList.toggle('active', slideIndex === index));
        }, 80);
      }

      handleTouchStart(event, image) {
        if (!this.mobileMediaQuery.matches) return;
        const state = this.zoomStates.get(image);
        if (!state) return;

        state.moved = false;
        if (event.touches.length === 2) {
          event.preventDefault();
          state.isPinching = true;
          state.isPanning = false;
          state.startDistance = this.touchDistance(event.touches);
          state.startScale = state.scale;
          image.classList.add('is-pinching');
          this.setZoomOrigin(image, event.touches[0], event.touches[1]);
        } else if (event.touches.length === 1 && state.scale > 1) {
          state.isPanning = true;
          state.startTouchX = event.touches[0].clientX;
          state.startTouchY = event.touches[0].clientY;
          state.startPanX = state.panX;
          state.startPanY = state.panY;
          image.classList.add('is-panning');
        }
      }

      handleTouchMove(event, image) {
        if (!this.mobileMediaQuery.matches) return;
        const state = this.zoomStates.get(image);
        if (!state) return;

        if (event.touches.length === 2 && state.isPinching) {
          event.preventDefault();
          const distance = this.touchDistance(event.touches);
          state.scale = this.clamp(state.startScale * (distance / Math.max(state.startDistance, 1)), 1, 4);
          state.moved = true;
          this.applyZoom(image, state);
        } else if (event.touches.length === 1 && state.isPanning && state.scale > 1) {
          event.preventDefault();
          const deltaX = event.touches[0].clientX - state.startTouchX;
          const deltaY = event.touches[0].clientY - state.startTouchY;
          const maxX = (image.clientWidth * (state.scale - 1)) / 2;
          const maxY = (image.clientHeight * (state.scale - 1)) / 2;
          state.panX = this.clamp(state.startPanX + deltaX, -maxX, maxX);
          state.panY = this.clamp(state.startPanY + deltaY, -maxY, maxY);
          state.moved = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5;
          this.applyZoom(image, state);
        }
      }

      handleTouchEnd(event, image) {
        if (!this.mobileMediaQuery.matches) return;
        const state = this.zoomStates.get(image);
        if (!state) return;

        if (state.isPinching) {
          if (event.touches.length < 2) this.finishTouch(image);
          if (state.scale < 1.05) this.resetZoom(image);
          return;
        }

        if (state.isPanning) {
          this.finishTouch(image);
          if (state.moved) return;
        }

        const touch = event.changedTouches[0];
        if (!touch || state.moved) return;
        const now = Date.now();
        const closeToLastTap = Math.hypot(touch.clientX - state.lastTapX, touch.clientY - state.lastTapY) < 36;
        if (now - state.lastTapTime < 320 && closeToLastTap) {
          event.preventDefault();
          this.toggleZoom(image, touch.clientX, touch.clientY);
          state.lastTapTime = 0;
        } else {
          state.lastTapTime = now;
          state.lastTapX = touch.clientX;
          state.lastTapY = touch.clientY;
        }
      }

      finishTouch(image) {
        const state = this.zoomStates.get(image);
        if (!state) return;
        state.isPinching = false;
        state.isPanning = false;
        image.classList.remove('is-pinching', 'is-panning');
      }

      toggleZoom(image, clientX, clientY) {
        const now = Date.now();
        if (now - this.lastZoomToggleAt < 220) return;
        this.lastZoomToggleAt = now;
        const state = this.zoomStates.get(image);
        if (!state) return;

        if (state.scale > 1.05) {
          this.resetZoom(image);
          return;
        }

        const rect = image.getBoundingClientRect();
        const originX = this.clamp(((clientX - rect.left) / Math.max(rect.width, 1)) * 100, 0, 100);
        const originY = this.clamp(((clientY - rect.top) / Math.max(rect.height, 1)) * 100, 0, 100);
        image.style.setProperty('--matchy-zoom-origin', `${originX}% ${originY}%`);
        state.scale = 2.5;
        state.panX = 0;
        state.panY = 0;
        this.applyZoom(image, state);
      }

      setZoomOrigin(image, firstTouch, secondTouch) {
        const rect = image.getBoundingClientRect();
        const centerX = (firstTouch.clientX + secondTouch.clientX) / 2;
        const centerY = (firstTouch.clientY + secondTouch.clientY) / 2;
        const originX = this.clamp(((centerX - rect.left) / Math.max(rect.width, 1)) * 100, 0, 100);
        const originY = this.clamp(((centerY - rect.top) / Math.max(rect.height, 1)) * 100, 0, 100);
        image.style.setProperty('--matchy-zoom-origin', `${originX}% ${originY}%`);
      }

      applyZoom(image, state) {
        image.style.setProperty('--matchy-zoom-scale', state.scale.toFixed(3));
        image.style.setProperty('--matchy-pan-x', `${state.panX.toFixed(1)}px`);
        image.style.setProperty('--matchy-pan-y', `${state.panY.toFixed(1)}px`);
        image.classList.toggle('is-zoomed', state.scale > 1.05);
        this.modalContent?.classList.toggle('is-zoomed', state.scale > 1.05);
      }

      resetZoom(image) {
        const state = this.zoomStates.get(image);
        if (!state) return;
        Object.assign(state, {
          scale: 1,
          startScale: 1,
          panX: 0,
          panY: 0,
          isPinching: false,
          isPanning: false,
          moved: false,
        });
        image.style.setProperty('--matchy-zoom-scale', '1');
        image.style.setProperty('--matchy-pan-x', '0px');
        image.style.setProperty('--matchy-pan-y', '0px');
        image.style.setProperty('--matchy-zoom-origin', '50% 50%');
        image.classList.remove('is-zoomed', 'is-pinching', 'is-panning');
        if (!this.modalContent?.querySelector('img.is-zoomed')) {
          this.modalContent?.classList.remove('is-zoomed');
        }
      }

      resetAllZoom(exceptImage = null) {
        this.modalContent?.querySelectorAll('img[data-media-id]').forEach((image) => {
          if (image !== exceptImage) this.resetZoom(image);
        });
        if (!exceptImage || !exceptImage.classList.contains('is-zoomed')) {
          this.modalContent?.classList.remove('is-zoomed');
        }
      }

      touchDistance(touches) {
        return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      }

      clamp(value, minimum, maximum) {
        return Math.min(maximum, Math.max(minimum, value));
      }
    }
  );
}
